import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Truck, MapPin, Clock, CheckCircle } from "@phosphor-icons/react";

const BASE = process.env.REACT_APP_BACKEND_URL;

export default function LiveEta() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  const load = () => axios.get(`${BASE}/api/public/eta/${token}`).then(r => setData(r.data)).catch(e => setErr(e.response?.data?.detail || "Not found"));
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, [token]);

  if (err) return <FullMsg emoji="🔗" title="ETA link expired" sub={err}/>;
  if (!data) return <FullMsg emoji="⏳" title="Loading…"/>;

  const arrived = data.status === "on_site" || data.status === "in_progress" || data.status === "completed";
  const stale = data.tech_updated_at && ((Date.now() - new Date(data.tech_updated_at).getTime()) > 5 * 60 * 1000);

  // Build a static OSM tile URL for a minimal free-tier map preview
  const mapImg = data.tech_lat && data.tech_lng
    ? `https://staticmap.openstreetmap.de/staticmap.php?center=${data.tech_lat},${data.tech_lng}&zoom=13&size=800x400&maptype=mapnik&markers=${data.tech_lat},${data.tech_lng},lightblue|${data.destination_lat},${data.destination_lng},red`
    : null;

  return (
    <div className="min-h-screen bg-slate-900 text-white p-6" data-testid="live-eta-page">
      <div className="max-w-lg mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <Truck weight="fill" size={32} className="text-sky-400"/>
          <div>
            <div className="text-[10px] font-black uppercase tracking-widest text-sky-400">Live ETA</div>
            <div className="font-black text-lg">{data.tech_name}</div>
          </div>
        </div>

        {arrived ? (
          <div className="bg-emerald-900/40 border-2 border-emerald-600 p-6 text-center" data-testid="live-eta-arrived">
            <CheckCircle weight="fill" size={48} className="mx-auto text-emerald-400 mb-2"/>
            <div className="text-xl font-black uppercase">Your tech is here</div>
          </div>
        ) : data.eta_minutes ? (
          <div className="bg-sky-900/40 border-2 border-sky-500 p-6 text-center" data-testid="live-eta-minutes">
            <div className="text-6xl font-black" data-testid="live-eta-value">{data.eta_minutes}</div>
            <div className="text-xs font-black uppercase tracking-widest text-sky-300 mt-1">minutes away</div>
            {stale && <div className="text-[10px] text-amber-400 mt-2">Position last updated more than 5 min ago</div>}
          </div>
        ) : (
          <div className="bg-slate-800 border-2 border-slate-700 p-6 text-center">
            <Clock size={32} className="mx-auto text-slate-400 mb-2"/>
            <div className="text-sm text-slate-300">Waiting for your tech to head out…</div>
          </div>
        )}

        <div className="mt-6 rugged-card p-4 bg-slate-800 border-2 border-slate-700" data-testid="live-eta-destination">
          <div className="flex items-start gap-2">
            <MapPin size={16} className="text-red-400 flex-shrink-0 mt-0.5"/>
            <div>
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-400">Heading to</div>
              <div className="font-bold text-sm">{data.destination_address}</div>
            </div>
          </div>
        </div>

        {mapImg && (
          <div className="mt-4 border-2 border-slate-700">
            <img src={mapImg} alt="Live tech position" className="w-full block" data-testid="live-eta-map"/>
          </div>
        )}

        <p className="mt-6 text-[10px] font-black uppercase tracking-widest text-slate-500 text-center">
          Auto-refreshes every 20 seconds
        </p>
      </div>
    </div>
  );
}

function FullMsg({ emoji, title, sub }) {
  return (
    <div className="min-h-screen bg-slate-900 text-white flex items-center justify-center p-6">
      <div className="text-center max-w-md">
        <div className="text-5xl mb-3">{emoji}</div>
        <h1 className="font-black text-2xl mb-2">{title}</h1>
        {sub && <p className="text-slate-400">{sub}</p>}
      </div>
    </div>
  );
}
